// mindmetal-app/web/src/pages/employee/Health.tsx
import { motion } from 'framer-motion'
import { Activity, RefreshCw, Clock, AlertCircle, Zap, Server } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent, Button } from '@personic/shared'
import { ServiceStatusCard } from '@/components/employee/ServiceStatusCard'
import { useServiceHealth, useSystemMetrics } from '@/hooks/useDevTools'

export default function Health() {
  const {
    data: services,
    isLoading: servicesLoading,
    refetch: refetchServices,
    isFetching: servicesFetching,
  } = useServiceHealth()

  const {
    data: metrics,
    isLoading: metricsLoading,
    refetch: refetchMetrics,
    isFetching: metricsFetching,
  } = useSystemMetrics()

  const isRefreshing = servicesFetching || metricsFetching

  const handleRefresh = () => {
    refetchServices()
    refetchMetrics()
  }

  const healthyCount = services?.filter((s) => s.status === 'healthy').length || 0
  const totalCount = services?.length || 0
  const overallStatus = healthyCount === totalCount ? 'All Systems Operational' : 'Some Issues Detected'

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center">
            <Activity className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-display font-bold text-midnight-900">System Health</h1>
            <p className="text-warm-500">Monitor service status and performance metrics</p>
          </div>
        </div>
        <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing}>
          <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </motion.div>

      {/* Overall Status Banner */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card
          variant="default"
          className={`border-l-4 ${healthyCount === totalCount ? 'border-l-emerald-500' : 'border-l-amber-500'}`}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div
                  className={`w-4 h-4 rounded-full ${healthyCount === totalCount ? 'bg-emerald-500' : 'bg-amber-500'}`}
                />
                <div>
                  <h2 className="text-lg font-semibold text-midnight-800">{overallStatus}</h2>
                  <p className="text-sm text-warm-500">
                    {healthyCount} of {totalCount} services healthy
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-warm-500">Last updated</p>
                <p className="text-sm font-mono text-midnight-700">
                  {new Date().toLocaleTimeString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Metrics Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="grid grid-cols-2 md:grid-cols-4 gap-4"
      >
        <Card variant="outlined" padding="sm">
          <CardContent className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50">
              <Zap className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-warm-500">API Calls (24h)</p>
              <p className="text-xl font-semibold text-midnight-800 font-mono">
                {metricsLoading ? '...' : metrics?.apiCallsToday?.toLocaleString() || '0'}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card variant="outlined" padding="sm">
          <CardContent className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-50">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-xs text-warm-500">Errors (24h)</p>
              <p className="text-xl font-semibold text-midnight-800 font-mono">
                {metricsLoading ? '...' : metrics?.errorsToday || '0'}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card variant="outlined" padding="sm">
          <CardContent className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-50">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-warm-500">Avg Latency</p>
              <p className="text-xl font-semibold text-midnight-800 font-mono">
                {metricsLoading ? '...' : `${metrics?.avgLatency || 0}ms`}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card variant="outlined" padding="sm">
          <CardContent className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-50">
              <Server className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-warm-500">Peak Usage</p>
              <p className="text-xl font-semibold text-midnight-800 font-mono">
                {metricsLoading ? '...' : metrics?.peakUsage || '--'}
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Service Status Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card>
          <CardHeader>
            <CardTitle>Service Status</CardTitle>
          </CardHeader>
          <CardContent>
            {servicesLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-copper-600" />
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {services?.map((service, index) => (
                  <motion.div
                    key={service.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + index * 0.05 }}
                  >
                    <ServiceStatusCard
                      name={service.name}
                      status={service.status}
                      uptime={service.uptime}
                      responseTime={service.responseTime}
                      lastCheck={new Date(service.lastCheck).toLocaleTimeString()}
                      errorCount={service.errorCount}
                    />
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Response Time Chart Placeholder */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card>
          <CardHeader>
            <CardTitle>Response Time Trend (24h)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48 flex items-center justify-center border border-dashed border-warm-300 rounded-xl">
              <div className="text-center text-warm-400">
                <Activity className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm">Response time chart</p>
                <p className="text-xs">Requires backend metrics endpoint</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Recent Errors */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
      >
        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle>Recent Errors</CardTitle>
            <a
              href="/employee/dev-tools/logs?level=ERROR"
              className="text-sm text-copper-600 hover:text-copper-700"
            >
              View All
            </a>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {/* Mock error entries */}
              <div className="p-3 bg-red-50 border border-red-100 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono text-red-600">distiller.claude</span>
                  <span className="text-xs text-warm-400">5 min ago</span>
                </div>
                <p className="text-sm text-midnight-800 font-mono">
                  Failed to connect to Anthropic API: timeout after 30s
                </p>
              </div>
              <div className="p-3 bg-red-50 border border-red-100 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono text-red-600">db.postgres</span>
                  <span className="text-xs text-warm-400">15 min ago</span>
                </div>
                <p className="text-sm text-midnight-800 font-mono">
                  Database connection pool exhausted
                </p>
              </div>
              <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono text-amber-600">kong.ratelimit</span>
                  <span className="text-xs text-warm-400">1 hour ago</span>
                </div>
                <p className="text-sm text-midnight-800 font-mono">
                  Rate limit approaching for tenant acme-corp (85%)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}
